export type ProposalReviseSectionId =
  | "presentation"
  | "overviews"
  | "commercial"
  | "history"
  | `equipment-${string}`;

export type ProposalReviseNavItem = {
  id: ProposalReviseSectionId;
  label: string;
};

export function reviseEquipmentSectionId(systemKey: string): ProposalReviseSectionId {
  return `equipment-${systemKey}`;
}

export function reviseSectionDomId(sectionId: ProposalReviseSectionId): string {
  return `revise-section-${sectionId}`;
}

export function buildReviseScrollNavItems(input: {
  equipmentOptions: Array<{ systemKey: string; label: string }>;
}): ProposalReviseNavItem[] {
  const items: ProposalReviseNavItem[] = input.equipmentOptions.map((opt) => ({
    id: reviseEquipmentSectionId(opt.systemKey),
    label: `${opt.label} — Equipment List`,
  }));
  items.push(
    { id: "presentation", label: "Proposal Content" },
    { id: "overviews", label: "System Overviews" },
    { id: "commercial", label: "Commercial Display" },
    { id: "history", label: "Revision History" },
  );
  return items;
}

/** @deprecated Panel tabs replaced by scroll-spy sections. */
export const PROPOSAL_REVISE_TABS = ["equipment", "presentation", "overviews", "commercial", "history"] as const;

export type ProposalReviseTabId = (typeof PROPOSAL_REVISE_TABS)[number];

export const PROPOSAL_REVISE_TAB_LABELS: Record<ProposalReviseTabId, string> = {
  equipment: "Equipment",
  presentation: "Presentation",
  overviews: "Overviews",
  commercial: "Commercial",
  history: "History",
};

export function reviseTabStorageKey(proposalId: number): string {
  return `clarity:revise-tab:${proposalId}`;
}

export function readStoredReviseTab(proposalId: number): ProposalReviseTabId | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(reviseTabStorageKey(proposalId));
    if (!raw) return null;
    return PROPOSAL_REVISE_TABS.includes(raw as ProposalReviseTabId) ? (raw as ProposalReviseTabId) : null;
  } catch {
    return null;
  }
}

export function storeReviseTab(proposalId: number, tab: ProposalReviseTabId): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(reviseTabStorageKey(proposalId), tab);
  } catch {
    // ignore quota errors
  }
}

export function isProposalApprovedOrWon(input: {
  status?: string | null;
  pipelineStage?: string | null;
  approvedAt?: string | Date | null;
}): boolean {
  if (input.status === "approved") return true;
  if (input.pipelineStage === "completed" || input.pipelineStage === "projected") return true;
  return Boolean(input.approvedAt);
}

export function parseProposalRevisionNumber(proposalRef: string): number {
  const match = /-R(\d+)$/i.exec(proposalRef.trim());
  return match ? Math.max(1, Number(match[1]) || 1) : 1;
}

export function nextProposalRevisionLabel(proposalRef: string): string {
  const n = parseProposalRevisionNumber(proposalRef) + 1;
  return `R${n}`;
}
